import * as THREE from 'three';
import { getGlowTexture } from './style.js';

// Each shooter fires its own kind of energy round.
const STYLES = {
  rocket_launcher: 'plasma',
  super_helicopter: 'plasma',
  sniper: 'rail',
  airplane: 'tracer'
};

// Geometries are shared by every projectile; materials are cached per colour.
// Bolts are modelled along +Z so Object3D.lookAt points them down the flight path.
const GEOMETRY = {
  boltCore: new THREE.CylinderGeometry(0.07, 0.07, 1.9, 6).rotateX(Math.PI / 2),
  boltGlow: new THREE.CylinderGeometry(0.2, 0.2, 2.5, 8).rotateX(Math.PI / 2),
  railCore: new THREE.CylinderGeometry(0.05, 0.05, 3.6, 6).rotateX(Math.PI / 2),
  railGlow: new THREE.CylinderGeometry(0.16, 0.16, 4.2, 8).rotateX(Math.PI / 2),
  // A unit-length beam from the origin back along -Z, scaled to the distance travelled
  streak: new THREE.CylinderGeometry(0.12, 0.03, 1, 6, 1, true).rotateX(Math.PI / 2).translate(0, 0, -0.5),
  tracerCore: new THREE.CylinderGeometry(0.06, 0.06, 0.9, 5).rotateX(Math.PI / 2),
  tracerGlow: new THREE.CylinderGeometry(0.16, 0.16, 1.2, 6).rotateX(Math.PI / 2),
  orbCore: new THREE.IcosahedronGeometry(0.32, 1),
  orbShell: new THREE.IcosahedronGeometry(0.62, 1)
};

const materials = new Map();
// Cores are solid so overlapping glows don't wash the team colour out to white
function material(key, color, opacity, additive = !key.endsWith('Core')) {
  const id = `${key}|${color.r.toFixed(3)},${color.g.toFixed(3)},${color.b.toFixed(3)}`;
  if (!materials.has(id)) {
    materials.set(id, new THREE.MeshBasicMaterial({
      color,
      transparent: true,
      opacity,
      blending: additive ? THREE.AdditiveBlending : THREE.NormalBlending,
      depthWrite: false,
      toneMapped: false
    }));
  }
  return materials.get(id);
}

// Hot near-white core tinted by the team, and a saturated outer glow
function coreColor(color) {
  return new THREE.Color(color).lerp(new THREE.Color(0xffffff), 0.12);
}
function glowColor(color) {
  return new THREE.Color(color).multiplyScalar(1.1);
}

export class Projectile {
  // shooterType is the shooter's unit type ('player', 'tank', 'sniper', ...) or 'turret'.
  constructor(scene, projectileData, color, shooterType = null) {
    this.scene = scene;
    this.data = projectileData;
    this.shooterType = shooterType;
    this.style = STYLES[shooterType] || 'bolt';
    this.color = new THREE.Color(color);
    this.mesh = null;
    this.trail = null;
    this.streak = null;
    this.trailPositions = [];
    this.maxTrailLength = this.style === 'plasma' ? 12 : 7;
    this.start = new THREE.Vector3(projectileData.position.x, projectileData.position.y, projectileData.position.z);
    this.create();
  }

  create() {
    this.mesh = new THREE.Group();
    const core = coreColor(this.color);
    const outer = glowColor(this.color);

    if (this.style === 'plasma') {
      this.createPlasma(core, outer);
    } else if (this.style === 'rail') {
      this.mesh.add(new THREE.Mesh(GEOMETRY.railCore, material('railCore', core, 1)));
      this.mesh.add(new THREE.Mesh(GEOMETRY.railGlow, material('railGlow', outer, 0.6)));
      this.addHalo(outer, 2.2, 0.75);
      // The streak hangs in the air behind the round, back to the muzzle
      this.streak = new THREE.Mesh(GEOMETRY.streak, new THREE.MeshBasicMaterial({
        color: outer, transparent: true, opacity: 0.55, blending: THREE.AdditiveBlending, depthWrite: false, toneMapped: false
      }));
      this.scene.add(this.streak);
    } else if (this.style === 'tracer') {
      // A short burst of three rounds in a row
      for (let i = 0; i < 3; i++) {
        const z = -i * 1.4;
        const c = new THREE.Mesh(GEOMETRY.tracerCore, material('tracerCore', core, 1));
        const g = new THREE.Mesh(GEOMETRY.tracerGlow, material('tracerGlow', outer, 0.7));
        c.position.z = z;
        g.position.z = z;
        this.mesh.add(c, g);
      }
      this.addHalo(outer, 1.6, 0.8);
    } else {
      this.mesh.add(new THREE.Mesh(GEOMETRY.boltCore, material('boltCore', core, 1)));
      this.mesh.add(new THREE.Mesh(GEOMETRY.boltGlow, material('boltGlow', outer, 0.65)));
      this.addHalo(outer, 2.0, 0.8);
    }

    this.updatePosition(this.data.position);
    this.orient();
    this.createTrail();

    this.scene.add(this.mesh);
  }

  addHalo(color, size, opacity) {
    const sprite = new THREE.Sprite(new THREE.SpriteMaterial({
      map: getGlowTexture(),
      color,
      transparent: true,
      opacity,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      toneMapped: false
    }));
    sprite.scale.setScalar(size);
    sprite.position.z = 0.6;
    this.mesh.add(sprite);
    this.halo = sprite;
    return sprite;
  }

  createPlasma(core, outer) {
    // Dreadnoughts lob bigger orbs than troopers
    const scale = this.shooterType === 'super_helicopter' ? 1.6 : 1.1;
    this.mesh.scale.setScalar(scale);
    this.core = new THREE.Mesh(GEOMETRY.orbCore, material('orbCore', core, 1));
    this.shell = new THREE.Mesh(GEOMETRY.orbShell, material('orbShell', outer, 0.45));
    this.mesh.add(this.core, this.shell);
    this.addHalo(outer, 3.2, 0.9);
    this.halo.position.z = 0;
    this.flickerSeed = Math.random() * 100;
  }

  // Point the round along its flight path (start -> end)
  orient() {
    const end = this.data.endPos;
    if (!end) return;
    const target = new THREE.Vector3(end.x, end.y, end.z);
    if (target.distanceToSquared(this.mesh.position) > 0.0001) {
      this.mesh.lookAt(target);
    }
  }

  // A glowing ribbon of points behind the round, fading with age
  createTrail() {
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(new Float32Array(this.maxTrailLength * 3), 3));
    geometry.setAttribute('color', new THREE.BufferAttribute(new Float32Array(this.maxTrailLength * 3), 3));
    this.trail = new THREE.Points(geometry, new THREE.PointsMaterial({
      size: this.style === 'plasma' ? 0.9 * this.mesh.scale.x : 0.55,
      map: getGlowTexture(),
      vertexColors: true,
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      toneMapped: false
    }));
    this.trail.frustumCulled = false;
    this.scene.add(this.trail);
    this.updateTrail(this.data.position);
  }

  updatePosition(position) {
    if (!this.mesh) return;
    this.mesh.position.set(position.x, position.y, position.z);

    if (this.core) {
      const t = performance.now() * 0.02 + this.flickerSeed;
      this.shell.scale.setScalar(1 + Math.sin(t) * 0.14 + Math.sin(t * 2.3) * 0.06);
      this.shell.rotation.set(t * 0.2, t * 0.3, 0);
      this.halo.material.opacity = 0.75 + Math.sin(t * 1.7) * 0.15;
    }

    if (this.streak) {
      // Stretch the streak from the muzzle to the round, capped so it stays a streak
      const distance = Math.min(this.start.distanceTo(this.mesh.position), 30);
      this.streak.position.copy(this.mesh.position);
      this.streak.quaternion.copy(this.mesh.quaternion);
      this.streak.scale.set(1, 1, Math.max(distance, 0.01));
    }

    this.updateTrail(position);
  }

  updateTrail(position) {
    if (!this.trail) return;

    // Only record new points when the projectile has actually moved
    const last = this.trailPositions[this.trailPositions.length - 1];
    if (last && Math.abs(last.x - position.x) + Math.abs(last.y - position.y) + Math.abs(last.z - position.z) < 0.05) {
      return;
    }
    // Fill in between server ticks so the trail reads as a continuous ribbon
    if (last) {
      const steps = 2;
      for (let s = 1; s < steps; s++) {
        const t = s / steps;
        this.trailPositions.push({
          x: last.x + (position.x - last.x) * t,
          y: last.y + (position.y - last.y) * t,
          z: last.z + (position.z - last.z) * t
        });
      }
    }
    this.trailPositions.push({ x: position.x, y: position.y, z: position.z });
    while (this.trailPositions.length > this.maxTrailLength) {
      this.trailPositions.shift();
    }

    const positions = this.trail.geometry.attributes.position;
    const colors = this.trail.geometry.attributes.color;
    const count = this.trailPositions.length;
    const c = new THREE.Color();
    const bright = glowColor(this.color);
    for (let i = 0; i < count; i++) {
      const p = this.trailPositions[i];
      positions.setXYZ(i, p.x, p.y, p.z);
      // Newest points are brightest; older ones fade to black (invisible when additive)
      const age = count > 1 ? 1 - i / (count - 1) : 0;
      c.copy(bright).multiplyScalar(1 - age * 0.9);
      colors.setXYZ(i, c.r, c.g, c.b);
    }
    positions.needsUpdate = true;
    colors.needsUpdate = true;
    this.trail.geometry.setDrawRange(0, count);
  }

  remove() {
    // Shared geometries and cached materials stay alive; only per-projectile resources are disposed
    if (this.mesh) {
      this.scene.remove(this.mesh);
      if (this.halo) this.halo.material.dispose();
      this.mesh = null;
      this.core = null;
      this.shell = null;
      this.halo = null;
    }

    if (this.streak) {
      this.scene.remove(this.streak);
      this.streak.material.dispose();
      this.streak = null;
    }

    if (this.trail) {
      this.scene.remove(this.trail);
      this.trail.geometry.dispose();
      this.trail.material.dispose();
      this.trail = null;
    }
  }
}
